import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Pencil, Sparkles, Loader2 } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { succLabel } from '@/lib/successionLabels';
import { useSuccGeneration } from '@/hooks/useSuccGeneration';

const SECTIONS = ['incumbent_challenges', 'incumbent_lessons', 'future_challenges',
  'critical_knowledge', 'legal_regulatory', 'key_relationships',
  'critical_decisions', 'transition_risks'];

export const SuccContinuityTab = ({ position, isES, reload }) => {
  const x = (es, en) => (isES ? es : en);
  const prof = position.continuity_profile || {};
  const [edit, setEdit] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const { gen, start: generate } = useSuccGeneration(position.id, 'continuity', isES, reload);

  const save = async (confirm) => {
    setBusy(true);
    try {
      await api.patch(`/client/succession/positions/${position.id}/continuity/${edit}`, { content: text, confirm });
      toast.success(confirm ? x('Sección confirmada.', 'Section confirmed.') : x('Borrador guardado.', 'Draft saved.'));
      setEdit(null); reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="succ-continuity-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold">{x('Perfil de Continuidad de la Posición', 'Position Continuity Profile')}</h3>
          <p className="text-xs text-muted-foreground">{x('Información estratégica para preservar el conocimiento y asegurar una transición efectiva.', 'Strategic information to preserve knowledge and secure an effective transition.')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={generate} disabled={gen} className="gap-1.5" data-testid="succ-continuity-generate">
          {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {gen ? x('Generando borrador…', 'Generating draft…') : x('Generar automáticamente', 'Generate automatically')}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SECTIONS.map((sec) => {
          const e = prof[sec] || {};
          return (
            <Card key={sec} className="p-4 shadow-card flex flex-col" data-testid={`succ-continuity-${sec}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="text-sm font-semibold leading-snug">{succLabel(isES, 'continuity', sec)}</h4>
                <Button variant="ghost" size="sm" className="h-7 px-2 shrink-0" data-testid={`succ-continuity-edit-${sec}`}
                  onClick={() => { setEdit(sec); setText(e.content || ''); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
              {e.content ? (
                <div className="text-sm text-foreground/85 whitespace-pre-line flex-1 leading-relaxed">{e.content}</div>
              ) : (
                <p className="text-xs text-muted-foreground italic flex-1">{x('Sin información registrada.', 'No information recorded.')}</p>
              )}
              {e.content && (
                <div className="mt-3 pt-2 border-t flex items-center gap-2 flex-wrap">
                  {e.status === 'confirmed' ? (
                    <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-[10px]">
                      {x('Confirmado por', 'Confirmed by')} {e.confirmed_by}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-[10px]">
                      {x('Borrador — pendiente de validación', 'Draft — pending validation')}
                    </Badge>
                  )}
                  <span className="text-[10px] text-muted-foreground">{e.updated_at}</span>
                </div>
              )}
              {sec === 'legal_regulatory' && e.content && (
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  {x('Borrador de planificación: no constituye asesoría legal y debe ser validado por el cliente.', 'Planning draft: not legal advice; must be validated by the client.')}
                </p>
              )}
            </Card>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {x('Esta información complementa la planificación de sucesión y debe mantenerse actualizada. Puede generarse como borrador automático; la validación final siempre es responsabilidad del cliente.',
           'This information complements succession planning and should be kept up to date. It can be drafted automatically; final validation is always the client\u2019s responsibility.')}
      </p>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[900px]" data-testid="succ-continuity-dialog">
          <DialogHeader><DialogTitle>{edit ? succLabel(isES, 'continuity', edit) : ''}</DialogTitle></DialogHeader>
          <Textarea rows={12} className="min-h-[16rem] max-h-[55vh] text-sm leading-relaxed" value={text} onChange={(e) => setText(e.target.value)} data-testid="succ-continuity-text" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>{x('Cancelar', 'Cancel')}</Button>
            <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="succ-continuity-save-draft">
              {x('Guardar borrador', 'Save draft')}
            </Button>
            <Button onClick={() => save(true)} disabled={busy} className="bg-gradient-primary hover:opacity-90" data-testid="succ-continuity-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Guardar y confirmar', 'Save and confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
